import { ThemeArtwork } from '@src/theme/Artwork';
import './NotificationAccessPage.css';
import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Container, HintError, Link, OtpChallenge, Panel, Shell, Stack, Typography } from '@src/components';
import {
  clearNotificationAccessToken, getNotificationAccessEmail, getNotificationAccessToken,
  recoverReasonFromError, setNotificationAccessEmail, setNotificationAccessReason,
} from '@src/access/notificationAccessToken';
import { santaClient } from '@src/state/client';
import { verifyOtpCode } from '@src/pages/auth/auth';

type Screen = 'inspecting' | 'continue' | 'recover' | 'code' | 'malformed';

export function NotificationAccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [screen, setScreen] = useState<Screen>(() => !getNotificationAccessToken() ? 'malformed'
    : pathname.replace(/\/$/, '') === '/open/recover' ? 'recover' : 'inspecting');
  const [notificationKind, setNotificationKind] = useState<'box_gift' | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState('');
  const inspecting = useRef<ReturnType<typeof santaClient.verifyInspect> | null>(null);

  function recover(message?: string) {
    const reason = recoverReasonFromError(message);
    if (reason === 'malformed') {
      clearNotificationAccessToken();
      setScreen('malformed');
    } else {
      setNotificationAccessReason(reason);
      setScreen('recover');
      navigate('/open/recover', { replace: true });
    }
  }

  useEffect(() => {
    const token = getNotificationAccessToken();
    if (!token || pathname.replace(/\/$/, '') === '/open/recover') return;
    let active = true;
    // Reuse inspection on StrictMode's second effect; consuming is only a click.
    inspecting.current ??= santaClient.verifyInspect({ token });
    void inspecting.current.then((result) => {
      if (!active) return;
      if (result.ok && result.data?.redirect) {
        clearNotificationAccessToken();
        window.location.assign(result.data.redirect);
      } else if (result.ok && result.data?.valid !== false) {
        setNotificationKind(result.data?.notificationKind);
        setScreen('continue');
      } else {
        if (result.data?.emailMasked) setNotificationAccessEmail(result.data.emailMasked);
        const message = result.ok
          ? `NOTIFICATION_ACCESS_${String(result.data?.reason || 'unavailable').toUpperCase()}`
          : result.error?.message;
        const reason = recoverReasonFromError(message);
        if (reason === 'malformed') {
          clearNotificationAccessToken();
          setScreen('malformed');
        } else {
          setNotificationAccessReason(reason);
          setScreen('recover');
          navigate('/open/recover', { replace: true });
        }
      }
    }).catch(() => {
      if (!active) return;
      setError('notificationAccess.failed');
      setScreen('recover');
      navigate('/open/recover', { replace: true });
    });
    return () => { active = false; };
  }, [pathname, navigate]);

  async function submit(sendCode: boolean) {
    if (busy) return;
    const token = getNotificationAccessToken();
    if (!token) { setScreen('malformed'); return; }
    setBusy(true);
    setError(null);
    try {
      if (sendCode) {
        const result = await santaClient.verifyRecover({ token });
        if (result.ok && result.data?.challengeToken) {
          setChallenge(result.data.challengeToken);
          setScreen('code');
        } else if (result.error?.status === 429 || result.error?.message === 'Too many requests') {
          setError('otp.rateLimited');
        } else {
          setError('notificationAccess.failed');
        }
      } else {
        const result = await santaClient.verifyConsume({ token });
        if (result.ok && result.data?.redirect) {
          clearNotificationAccessToken();
          window.location.assign(result.data.redirect);
        } else if (result.error?.status === 429 || result.error?.message === 'Too many requests') {
          setError('otp.rateLimited');
        } else {
          if (result.error?.message?.startsWith('NOTIFICATION_ACCESS_')) recover(result.error.message);
          else setError('notificationAccess.failed');
        }
      }
    } catch {
      setError('notificationAccess.failed');
    } finally {
      setBusy(false);
    }
  }

  const isGift = screen === 'continue' && notificationKind === 'box_gift';
  const title = isGift ? t('notificationAccess.giftTitle') : screen === 'recover' ? t('notificationAccess.recoverTitle')
    : screen === 'malformed' ? t('notificationAccess.malformedTitle')
      : screen === 'code' ? t('otp.title') : t('notificationAccess.title');
  const email = getNotificationAccessEmail();

  return (
    <Shell header={false} footer={false}>
      <Container as="main" width="auth" gutter="narrow" pad="page" grow className={isGift ? 'notification-access--gift' : undefined} testId="notification-access">
        <Stack gap={8} wide>
          <PageHeading title={<>{title}</>} />
          <Panel padding="large" elevated edges={{ pieces: 3 }} className={isGift ? 'notification-access__gift-panel' : undefined}>
            <Stack gap={6}>
              {isGift && <div className="notification-access__gift-welcome" data-testid="notification-gift-welcome">
                <div className="notification-access__gift-art" aria-hidden="true">
                  <span className="notification-access__sparkle notification-access__sparkle--1">✦</span>
                  <span className="notification-access__sparkle notification-access__sparkle--2">✧</span>
                  <span className="notification-access__sparkle notification-access__sparkle--3">✦</span>
                  <span className="notification-access__sparkle notification-access__sparkle--4">✧</span>
                  <ThemeArtwork role="gift" className="notification-access__present" />
                </div>
                <p className="notification-access__gift-intro">{t('notificationAccess.giftIntro')}</p>
                <p className="notification-access__gift-hint">{t('notificationAccess.giftHint')}</p>
              </div>}
              {screen === 'inspecting'  && <Typography as="p" role="body/base" aria-live="polite">{t('notificationAccess.inspecting')}</Typography>}
              {screen === 'recover' && <>
                <Typography as="p" role="body/base">{t('notificationAccess.recoverHint')}</Typography>
                <Typography as="p" role="body/sm">{t(email ? 'notificationAccess.destination' : 'notificationAccess.destinationPlain', { email })}</Typography>
              </>}
              {screen === 'malformed' && <Typography as="p" role="body/base">{t('notificationAccess.malformedHint')}</Typography>}
              {screen === 'code' && <OtpChallenge
                challengeToken={challenge}
                hint={t('notificationAccess.codeHint')}
                testId="notification-code"
                onVerify={async (token, code) => {
                  try {
                    const failure = await verifyOtpCode(navigate, token, code);
                    if (!failure) clearNotificationAccessToken();
                    return failure;
                  } catch { return { key: 'notificationAccess.failed' }; }
                }}
              />}
              <HintError error={error} testId="notification-error" />
              {screen === 'continue' && <Button fullWidth size="large" disabled={busy} testId="notification-continue" onClick={() => void submit(false)}>{t(isGift ? 'notificationAccess.giftContinue' : 'notificationAccess.continueLabel')}</Button>}
              {(screen === 'recover' || screen === 'code') && <Button fullWidth size="large" disabled={busy} testId="notification-send" onClick={() => void submit(true)}>{t(screen === 'code' ? 'notificationAccess.resend' : 'notificationAccess.send')}</Button>}
              {screen === 'malformed' && <Button fullWidth onClick={() => navigate('/')} testId="notification-home">{t('notificationAccess.home')}</Button>}
              {screen !== 'inspecting' && <Link href="/login" variant="quiet">{t('notificationAccess.login')}</Link>}
            </Stack>
          </Panel>
        </Stack>
      </Container>
    </Shell>
  );
}
